/**
 * Threekit Product Configurator - Native Player API
 * For Shopify Impact Theme (Attachments & Composite Logic)
 */

document.addEventListener('DOMContentLoaded', async function () {
  if (!window.builderProduct) return;

  const {
    assetId,
    authToken,
    moneyFormat,
    badgeLabel,
    partsMap = {},
    discountPercent = 0
  } = window.builderProduct;

  const LEGACY_FALLBACK_TOKEN = 'a7a36697-7234-4de7-ae39-fe41f5623049';
  const THREEKIT_AUTH_TOKEN = authToken || LEGACY_FALLBACK_TOKEN;
  const PLACEHOLDER_IMAGE = 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';

  // Placeholder SKU that Threekit outputs for weight stacks when the data table
  // doesn't have a composite SKU configured. We replace it with the correct one.
  const WEIGHT_STACK_PLACEHOLDER_SKU = '555-3381';

  let player;
  let extension;
  let syncTimer;
  let currentCartPayload = [];

  window.localUIState = window.localUIState || {};

  const viewState = {
    currentView: 'main',
    tempSelection: null
  };

  window.builderFailedImages = window.builderFailedImages || new Set();

  try {
    if (!assetId) {
      throw new Error('Missing Threekit asset id.');
    }

    // Show skeleton immediately — replaces the spinner div
    const uiRoot = document.getElementById('builder-ui-root');
    if (uiRoot) uiRoot.innerHTML = buildSkeletonHTML();

    player = await window.threekitPlayer({
      authToken: THREEKIT_AUTH_TOKEN,
      el: document.getElementById('threekit-player'),
      assetId,
      showConfigurator: false
    });

    extension = await waitForThreekitExtension();

    console.log('Waiting for default Threekit model to settle...');

    await refreshBuilderState('initial-load', { forceMainView: false });

    // Only NOW reveal the player — after state is fully settled
    const fallbackMedia = document.getElementById('builder-fallback-media');
    const playerDiv = document.getElementById('threekit-player');
    if (fallbackMedia && playerDiv) {
      fallbackMedia.style.display = 'none';
      playerDiv.style.position = 'relative';
      playerDiv.style.opacity = '1';
      playerDiv.style.pointerEvents = 'auto';
    }

    const hydrated = await hydrateStateFromUrl();
    if (hydrated) {
      // Older shared links may have "1 stack" without a position — apply the default.
      await ensureDefaultSingleStackPosition();
      await refreshBuilderState('url-hydration', { forceMainView: false });
    }

    if (typeof extension.onConfigurationChange === 'function') {
      extension.onConfigurationChange(() => triggerSync());
    } else if (typeof extension.on === 'function') {
      extension.on('configurationChange', () => triggerSync());
    }

    const addToCartBtn = document.getElementById('builder-add-to-cart');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', handleAddToCart);
    }
  } catch (err) {
    showError('Failed to load 3D Viewer.');
  }


  async function waitForThreekitExtension() {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const checkInterval = setInterval(() => {
        attempts += 1;
        let ext =
          window.dataDrivenConfiguratorExtension ||
          window.dataDrivenConfigurator;
        if (!ext && window.threekitMainPlayerFrameWindowElement) {
          ext = window.threekitMainPlayerFrameWindowElement.dataDrivenConfiguratorExtension;
        }
        if (!ext) {
          const iframe = document.querySelector('#threekit-player iframe');
          if (iframe && iframe.contentWindow) {
            try {
              ext = iframe.contentWindow.dataDrivenConfiguratorExtension ||
                    iframe.contentWindow.dataDrivenConfigurator;
            } catch(e) {}
          }
        }
        if (ext && typeof ext.getStatus === 'function') {
          clearInterval(checkInterval);
          resolve(ext);
        } else if (attempts >= 240) {
          clearInterval(checkInterval);
          reject(new Error('Threekit took too long to load.'));
        }
      }, 250);
    });
  }

  async function hydrateStateFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const rawEntries = [];

    for (const [key, value] of urlParams.entries()) {
      if (key === 'variant' || key === 'tkconfig') continue;
      rawEntries.push({ key, value });
      window.localUIState[key] = value;
    }

    if (rawEntries.length === 0) return false;

    // Apply attrs in dependency order — parents before children.
    // Position/dependent attrs are set AFTER their enabling parent.
    // This prevents "No item named X" crashes from Threekit's rule engine
    // trying to set a child attr before its parent has been activated.
    const APPLY_ORDER = [
      // Base structure first
      'Uprights_Color', 'Crossbar', 'Rear_Crossbar_WC', 'Front_Foot_Stabilizers_POS_DC',
      // Binary toggles (parents) before their positions (children)
      'Monkey_Bar',
      'Smith_Machine',
      'Spotter_Arms',
      'J-Hooks_Type1',
      'J-Hooks_Type4',
      // Weight system before stack amount/positions
      'Weight_System', 'Weight_Stack_Amount',
      // Storage
      'Weight_Pins_20cm_Amount', 'Weight_Pins_30cm_Amount',
      // Positions / dependent attrs last
      'Position_WC1',
      'Smith_Position',
      'Spotter_Arms_Position',
      'J-Hooks_Type1_Position',
      'J-Hooks_Type4_Position',
      'Weight_Stack1_Position',
      'Weight_Stack2_Position',
    ];

    // Sort entries: known order first, then anything else, positions always last
    const positionAttrs = new Set([
      'Position_WC1', 'Smith_Position', 'Spotter_Arms_Position',
      'J-Hooks_Type1_Position', 'J-Hooks_Type4_Position',
      'Weight_Stack1_Position', 'Weight_Stack2_Position',
    ]);

    const sortedEntries = [...rawEntries].sort((a, b) => {
      const ai = APPLY_ORDER.indexOf(a.key);
      const bi = APPLY_ORDER.indexOf(b.key);
      // Unknown attrs go in middle, position attrs always last
      const aIdx = ai >= 0 ? ai : (positionAttrs.has(a.key) ? 999 : 50);
      const bIdx = bi >= 0 ? bi : (positionAttrs.has(b.key) ? 999 : 50);
      return aIdx - bIdx;
    });

    // Apply each entry sequentially, waiting for Threekit to settle.
    // Get status once between each call — don't call getHeavyStatus 15 times.
    for (const entry of sortedEntries) {
      // Wait for Threekit to be ready (fast poll, not heavy)
      let currentStatus = null;
      for (let i = 0; i < 20; i++) {
        try {
          let s = extension.getStatus();
          if (s instanceof Promise) s = await s;
          if (s && s.validAttributesAndTheirValues) { currentStatus = s; break; }
        } catch(e) {}
        await new Promise(r => setTimeout(r, 150));
      }

      if (!currentStatus) {
        console.warn(`Hydrate: timed out waiting for status before setting ${entry.key}`);
        continue;
      }

      // Check if the attr is currently exposed by Threekit.
      // Skip if not — it may be a dependent attr whose parent isn't set yet,
      // or it was set in a previous iteration and will unlock it.
      const attrDef = currentStatus.validAttributesAndTheirValues
                        .find(a => a.name === entry.key);

      if (!attrDef) {
        // For position attrs, we can skip — they'll be set after
        // their parent is applied in a later iteration.
        if (positionAttrs.has(entry.key)) {
          console.warn(`Hydrate: skipping ${entry.key} (parent not yet active)`);
          continue;
        }
        // For non-position attrs not found, try setting anyway
        // (some attrs only appear after initial load completes)
      }

      let payloadVal = entry.value;
      if (attrDef && Array.isArray(attrDef.values)) {
        const selectedObj = attrDef.values.find(v =>
          String(v.name) === String(entry.value) ||
          String(v.displayName) === String(entry.value)
        );
        if (selectedObj) payloadVal = getThreekitPayloadValue(selectedObj);
      }

      try {
        await extension.setConfiguration({ [entry.key]: payloadVal });
        // Short settle time — enough for rule engine to process one change
        await new Promise(r => setTimeout(r, 200));
      } catch (err) {
        console.warn(`Hydrate: skipped ${entry.key} —`, err.message);
      }
    }

    return true;
  }

  function triggerSync() {
    clearTimeout(syncTimer);

    syncTimer = setTimeout(async () => {
      try {
        await refreshBuilderState('configurationChange');
      } catch (err) {
        console.error('triggerSync error:', err);
      }
    }, 350);
  }

  async function getHeavyStatus(retries = 15, delayMs = 250) {
    for (let i = 0; i < retries; i++) {
      try {
        let status = extension.getStatus();
        if (status instanceof Promise) status = await status;

        if (status && status.validAttributesAndTheirValues) {
          return status;
        }
      } catch (e) {
        console.warn(`Heavy load computing... (${i + 1}/${retries})`);
      }
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
    return null;
  }

  async function getFastStatus(retries = 5, delayMs = 50) {
    for (let i = 0; i < retries; i++) {
      try {
        let status = extension.getStatus();
        if (status instanceof Promise) status = await status;
        if (status && status.validAttributesAndTheirValues) {
          return status;
        }
      } catch (e) {}
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
    return null;
  }

  async function refreshBuilderState(reason, options = {}) {
    const { forceMainView = false } = options;
    
    const isHeavyLoad = reason === 'initial-load' || reason === 'url-hydration';
    const status = isHeavyLoad ? await getHeavyStatus() : await getFastStatus();

    if (!status || !status.validAttributesAndTheirValues) return null;

    syncLocalUiState(status);

    if (forceMainView) {
      viewState.currentView = 'main';
    }
    renderUI(status);
    updatePrice(status);
    publishSelection(status, reason);
    return status;
  }

  // ✨ HELPER: Checks if an attribute is actually visible in your Shopify UI Map to block Ghosts
  function isAttributeInUiMap(attrName) {
    if (!window.BUILDER_SECTION_MAP) return true;
    for (const subsections of Object.values(window.BUILDER_SECTION_MAP)) {
      for (const tkNames of Object.values(subsections)) {
        if (Array.isArray(tkNames) && tkNames.includes(attrName)) {
          return true;
        }
      }
    }
    return false;
  }

  function syncLocalUiState(status) {
    const attributes = status.validAttributesAndTheirValues || [];
    const currentConfig = status.attributes || {};
    const activeSkus = new Set(collectActiveStatusSkus(status));

    attributes.forEach(attr => {
      if (viewState.currentView === attr.name) return;
      // Mutex attrs (J-Hooks) are controlled entirely by the toggle handler + localUIState.
      // Never let syncLocalUiState overwrite them with Threekit's (potentially stale) state.
      if ((window.BUILDER_MUTEX_PAIRS || {})[attr.name] !== undefined) return;

      const selectedOption = resolveSelectedOption(attr, currentConfig[attr.name], activeSkus, status);

      if (!selectedOption || isOptionEffectivelyOff(selectedOption)) {
        window.localUIState[attr.name] = null;
        return;
      }

      const selectedLabel =
        selectedOption.displayName ||
        selectedOption.name ||
        selectedOption.value ||
        null;

      if (selectedLabel) {
        window.localUIState[attr.name] = String(selectedLabel);
      }
    });

    // Mutex enforcement: if both J-Hook partners appear active, use status.skus to correct
    const mutexPairs = window.BUILDER_MUTEX_PAIRS || {};
    const checkedPairs = new Set();
    Object.entries(mutexPairs).forEach(([tkName, partner]) => {
      const pairKey = [tkName, partner].sort().join('|');
      if (checkedPairs.has(pairKey)) return;
      checkedPairs.add(pairKey);
      const aIsOn = !!(window.localUIState[tkName] && !isOffLikeValue(window.localUIState[tkName]));
      const bIsOn = !!(window.localUIState[partner] && !isOffLikeValue(window.localUIState[partner]));
      if (!aIsOn || !bIsOn) return;
      const skuKeyA = (window.BUILDER_ATTR_SKU_KEY || {})[tkName];
      const skuKeyB = (window.BUILDER_ATTR_SKU_KEY || {})[partner];
      const skuA = skuKeyA ? status?.skus?.[skuKeyA] : null;
      const skuB = skuKeyB ? status?.skus?.[skuKeyB] : null;
      const aHasSku = skuA && skuA !== 'N/A';
      const bHasSku = skuB && skuB !== 'N/A';
      if (aHasSku && !bHasSku) { window.localUIState[partner] = null; }
      else if (bHasSku && !aHasSku) { window.localUIState[tkName] = null; }
      else { window.localUIState[tkName] = null; window.localUIState[partner] = null; }
    });
  }

  function resolveSelectedOption(attr, configValue, activeSkus, status) {
    const values = attr?.values || [];
    if (!values.length) return null;

    const explicitSelected = values.find(option => option?.selected === true);
    if (explicitSelected && !isOptionEffectivelyOff(explicitSelected)) {
      return explicitSelected;
    }

    const configMatched = values.find(option => isOptionSelectedByConfig(option, configValue));
    if (configMatched && !isOptionEffectivelyOff(configMatched)) {
      return configMatched;
    }

    const skuMatched = values
      .map(option => ({
        option,
        matchedCount: getOptionSkus(attr, option, status).filter(sku => activeSkus.has(sku)).length
      }))
      .filter(entry => entry.matchedCount > 0 && !isOptionEffectivelyOff(entry.option))
      .sort((a, b) => b.matchedCount - a.matchedCount)[0]?.option;

    if (skuMatched && !isOptionEffectivelyOff(skuMatched)) {
      return skuMatched;
    }

    return null;
  }

  async function updateThreekit(attrName, payloadValue) {
    try {
      const payload = { [attrName]: payloadValue };
      const req = extension.updateConfiguration
        ? extension.updateConfiguration(payload)
        : extension.setConfiguration(payload);

      if (req instanceof Promise) await req;
    } catch (e) {
      console.error('TK Update Error:', e);
      // Threekit rule engine can throw on certain config combinations
      // (e.g. clearing weight stack positions). Trigger a sync to recover UI state.
      triggerSync();
    }
  }

  function getEmptyPayload(attrName, attributes) {
    const attr = (attributes || []).find(a => a.name === attrName);
    if(!attr || !Array.isArray(attr.values)) return '';

    const zeroOpt = attr.values.find(v =>
      isZeroLikeValue(v.name) || isZeroLikeValue(v.value)
    );
    if (zeroOpt) return getThreekitPayloadValue(zeroOpt);

    const offOpt = attr.values.find(v =>
      isOffLikeValue(v.name) || isOffLikeValue(v.value)
    );

    if (offOpt) return getThreekitPayloadValue(offOpt);

    const firstOpt = attr.values.find(v => v.visible !== false && v.enabled !== false);
    if (firstOpt) return getThreekitPayloadValue(firstOpt);

    return '';
  }

  // ─── DEFAULT POSITION FOR A SINGLE WEIGHT STACK ─────────────────────────
  // With "Number of Weight Stacks" = 1, Threekit exposes Weight_Stack1_Position (Left)
  // and Weight_Stack2_Position (Right) with nothing selected, so the customer ends up
  // with no stack positioned (no composite SKU, nothing shown in the 3D view).
  // Default to Right Front (DCR2); fall back to Left Front (DCL2) if Right Front is
  // not available. Does nothing if a position is already set.
  const SINGLE_STACK_DEFAULTS = [
    { attr: 'Weight_Stack2_Position', code: 'DCR2', other: 'Weight_Stack1_Position' },
    { attr: 'Weight_Stack1_Position', code: 'DCL2', other: 'Weight_Stack2_Position' },
  ];

  function isStackPositionSet(status, attrName) {
    const raw = status?.attributes?.[attrName];
    const rawStr = (raw && typeof raw === 'object')
      ? String(raw.assetId || raw.value || raw.name || '')
      : String(raw ?? '');
    if (rawStr && !isOffLikeValue(rawStr)) return true;
    const attr = (status?.validAttributesAndTheirValues || []).find(a => a.name === attrName);
    return !!attr?.values?.some(v => v.selected && !isOptionEffectivelyOff(v));
  }

  async function ensureDefaultSingleStackPosition() {
    try {
      if (String(window.localUIState?.Weight_Stack_Amount) !== '1') return false;

      // Give Threekit's rule engine a moment to expose the position attributes
      // after the amount change.
      let status = null;
      let target = null;
      let targetOpt = null;
      for (let i = 0; i < 10 && !target; i++) {
        status = await getFastStatus();
        const attrs = status?.validAttributesAndTheirValues || [];
        for (const cand of SINGLE_STACK_DEFAULTS) {
          const a = attrs.find(x => x.name === cand.attr);
          if (!a || a.visible === false || a.enabled === false) continue;
          const opt = (a.values || []).find(v =>
            v.visible !== false && v.enabled !== false && !isOptionEffectivelyOff(v) &&
            (String(v.name).toUpperCase() === cand.code || String(v.value).toUpperCase() === cand.code)
          );
          if (opt) { target = cand; targetOpt = opt; break; }
        }
        if (!target) await new Promise(r => setTimeout(r, 150));
      }
      if (!target || !targetOpt) return false;

      // A position is already set (by the user, a saved link, or Threekit) — leave it.
      if (isStackPositionSet(status, 'Weight_Stack1_Position') ||
          isStackPositionSet(status, 'Weight_Stack2_Position') ||
          (window._builderPositionOverride || {})['Weight_Stack1_Position'] ||
          (window._builderPositionOverride || {})['Weight_Stack2_Position']) {
        return false;
      }

      const value = String(targetOpt.name);
      window.localUIState[target.attr] = value;
      window._builderPositionOverride = window._builderPositionOverride || {};
      window._builderPositionOverride[target.attr] = value;
      delete window._builderPositionOverride[target.other];
      console.log(`[stack-default] 1 stack selected, defaulting ${target.attr} = ${value}`);
      await updateThreekit(target.attr, getThreekitPayloadValue(targetOpt));
      return true;
    } catch (e) {
      console.warn('ensureDefaultSingleStackPosition:', e);
      return false;
    }
  }

  // Hide those three footer div for 2nd and 3nd options panel
  function updateDrilldownClass() {
    const sidebar = document.querySelector('.builder-sidebar');
    if (!sidebar) return;
    if (viewState.currentView === 'main') {
      sidebar.classList.remove('builder-in-drilldown');
    } else {
      sidebar.classList.add('builder-in-drilldown');
    }
  }


  function renderUI(status) {
    const root = document.getElementById('builder-ui-root');
    if (!root) return;
    const sidebar = document.querySelector('.builder-sidebar');
    if (sidebar) sidebar.classList.remove('builder-in-drilldown');
    const headerWrapper = document.getElementById('builder-header-wrapper');
    if (headerWrapper) headerWrapper.style.display = 'block';
    const attributes = status.validAttributesAndTheirValues || [];
    const currentConfig = status.attributes || {};
    const hiddenAttrs = window.BUILDER_HIDDEN_ATTRS || new Set();
    const colorSwatches = window.BUILDER_COLOR_SWATCHES || {};
    const chipAttrs = window.BUILDER_CHIP_ATTRS || new Set();
    if (!viewState.expandedSections) {
      const firstSection = Object.keys(window.BUILDER_SECTION_MAP || {})[0];
      viewState.expandedSections = new Set(firstSection ? [firstSection] : []);
    }
    if (viewState.expandedInlineAttr === undefined) viewState.expandedInlineAttr = null;
    let html = '';
    for (const [sectionName, subsections] of Object.entries(window.BUILDER_SECTION_MAP || {})) {
      const isOpen = viewState.expandedSections.has(sectionName);
      let selCount = 0;
      for (const attrNames of Object.values(subsections)) {
        for (const tkName of attrNames) {
          if (hiddenAttrs.has(tkName)) continue;
          const sa = String(window.localUIState?.Weight_Stack_Amount || '0');
          if (tkName === 'Weight_Stack2_Position' && sa === '2') continue;
          const v = window.localUIState[tkName];
          if (v && !isOffLikeValue(v)) selCount++;
        }
      }
      html += `<div class="bld-section" data-section="${escapeHtml(sectionName)}">`;
      html += `<div class="bld-section-head" data-section="${escapeHtml(sectionName)}" role="button"><div class="bld-section-title">${escapeHtml(sectionName)}${selCount > 0 ? `<span class="bld-sel-count">${selCount}</span>` : ''}</div><div style="display:flex;align-items:center;gap:8px;">${selCount > 0 ? `<div class="bld-clear-section" data-section="${escapeHtml(sectionName)}" role="button" title="Clear all"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></div>` : ''}<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">${isOpen ? `<polyline points="18 15 12 9 6 15"></polyline>` : `<polyline points="6 9 12 15 18 9"></polyline>`}</svg></div></div>`;
      if (isOpen) {
        html += `<div class="bld-section-body">`;
        for (const [, attrNames] of Object.entries(subsections)) {
          for (const tkName of attrNames) {
            if (hiddenAttrs.has(tkName)) continue;
            const stackAmount = String(window.localUIState?.Weight_Stack_Amount || status.attributes?.Weight_Stack_Amount || '0');
            // Weight_Stack2_Position: hidden when amount=2 (combined row used instead),
            // shown when amount=1 so user can choose Right-side position.
            if (tkName === 'Weight_Stack2_Position' && stackAmount === '2') continue;
            const overrideLabel = (tkName === 'Weight_Stack1_Position' && stackAmount === '2') ? 'Weight Stack Position' : null;
            const attr = attributes.find(a => a.name === tkName);
            const neverLock = tkName === 'Weight_Pins_30cm_Amount' || tkName === 'J-Hooks_Type4';
            let isLocked = false, lockHint = '';
            if (!neverLock) {
              if (!attr || attr.visible === false || attr.enabled === false) { isLocked = true; lockHint = (window.BUILDER_DEPENDENCY_HINTS || {})[tkName] || 'Complete selections above to enable'; }
              else { const rv = String(currentConfig[attr.name] || '').toLowerCase(); if (rv === 'tkdatadrivenconfiguratortooldataattributeunavailable') { isLocked = true; lockHint = (window.BUILDER_DEPENDENCY_HINTS || {})[tkName] || 'Complete selections above to enable'; } }
              if (tkName === 'Front_Foot_Stabilizers_POS_DC' && !isLocked) { const cbVal = window.localUIState?.Crossbar || status.attributes?.Crossbar || attributes.find(a => a.name === 'Crossbar')?.values?.find(v => v.selected)?.name; if (String(cbVal) !== '42') { isLocked = true; lockHint = (window.BUILDER_DEPENDENCY_HINTS || {})['Front_Foot_Stabilizers_POS_DC'] || 'Select Short (42cm) crossbar first'; } }
            }
            const uiTitle = overrideLabel || formatName(window.translateTKName(tkName));
            if (isLocked) { html += `<div class="bld-row bld-row-disabled" data-tooltip="${escapeHtml(lockHint)}"><span class="bld-row-title">${escapeHtml(uiTitle)}</span><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="opacity:.35"><polyline points="9 18 15 12 9 6"></polyline></svg></div>`; continue; }
            if (!attr) continue;
            const validOptions = (attr.values || []).filter(v => v.visible !== false && v.enabled !== false && String(v.name).toLowerCase() !== 'tkdatadrivenconfiguratortooldataattributeunavailable');
            if (!neverLock && (validOptions.length === 0 || validOptions.every(v => isOffLikeValue(v.name)))) continue;
            const activeVal = window.localUIState[tkName];
            const isSelected = !!(activeVal && !isOffLikeValue(activeVal));
            const nonOffOptions = validOptions.filter(v => !['no','none'].includes(String(v.name).toLowerCase()));
            if (tkName === 'Uprights_Color') {
              let colorPrice = ''; if (activeVal) { const selOpt = validOptions.find(v => String(v.name) === String(activeVal)); if (selOpt) { const od = resolveOptionData(attr, selOpt, status); if (od.priceText) colorPrice = od.priceText; } }
              html += `<div class="bld-color-row"><div class="bld-swatches">${nonOffOptions.map(val => { const sw = colorSwatches[val.name] || '#888'; const isSel = String(activeVal) === String(val.name); return `<div class="bld-swatch ${isSel ? 'selected' : ''}" data-color-attr="${encodeURIComponent('Uprights_Color')}" data-val-name="${escapeHtml(String(val.name))}" title="${escapeHtml(String(val.name))}" role="button" style="background:${escapeHtml(sw)};">${isSel ? `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3"><polyline points="20 6 9 17 4 12"></polyline></svg>` : ''}</div>`; }).join('')}</div>${activeVal ? `<span class="bld-color-label">${escapeHtml(activeVal)}</span>` : ''}${colorPrice ? `<span class="bld-color-price">${escapeHtml(colorPrice)}</span>` : ''}</div>`; continue;
            }
            if (chipAttrs.has(tkName)) {
              const stripSide = tkName === 'Weight_Stack1_Position' && stackAmount === '2';
              html += `<div class="bld-chip-row-wrap"><div class="bld-chip-label">${escapeHtml(uiTitle)}</div><div class="bld-chips-row">${nonOffOptions.map(val => { const isSel = String(activeVal) === String(val.name); let label = formatName(window.translateTKName(val.displayName || val.name || '')); if (stripSide) label = label.replace(/^(Left|Right)\s+/i, ''); return `<div class="bld-chip ${isSel ? 'selected' : ''}" data-chip-attr="${encodeURIComponent(tkName)}" data-val-name="${escapeHtml(String(val.name))}" role="button">${escapeHtml(label)}</div>`; }).join('')}</div></div>`; continue;
            }
            const isAmountAttr = tkName.toLowerCase().includes('_amount') && !chipAttrs.has(tkName);
            if (isAmountAttr) {
              const isExpanded = viewState.expandedInlineAttr === tkName;
              html += `<div class="bld-row bld-accordion-row ${isSelected ? 'active' : ''} ${isExpanded ? 'expanded' : ''}" data-inline-attr="${encodeURIComponent(tkName)}" role="button"><div class="bld-row-title">${escapeHtml(uiTitle)}${isSelected ? `<span class="bld-row-sub">${escapeHtml(formatName(window.translateTKName(activeVal)))}</span>` : ''}</div><div style="display:flex;align-items:center;gap:6px;">${isSelected ? `<div class="bld-x-btn" data-clear-attr="${encodeURIComponent(tkName)}" role="button"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></div>` : ''}<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">${isExpanded ? `<polyline points="18 15 12 9 6 15"></polyline>` : `<polyline points="6 9 12 15 18 9"></polyline>`}</svg></div></div>`;
              if (isExpanded) {
                html += `<div class="bld-tile-grid">`;
                nonOffOptions.forEach(val => { if (['0'].includes(String(val.name))) return; const od = resolveOptionData(attr, val, status); const hasImg = od.imgSrc && od.imgSrc !== PLACEHOLDER_IMAGE && !window.builderFailedImages.has(od.imgSrc); const label = formatName(window.translateTKName(val.displayName || val.name || '')); const isSel = String(activeVal) === String(val.name); const qty = parseInt(String(val.name), 10) || 1; const displayPrice = od.shopifyData?.price ? formatMoney(Number(od.shopifyData.price) * qty) : od.priceText; html += `<div class="bld-tile ${isSel ? 'selected' : ''}" data-inline-attr="${encodeURIComponent(tkName)}" data-val-name="${escapeHtml(String(val.name || ''))}" role="button"><div class="bld-tile-img">${hasImg ? `<img src="${escapeAttributeValue(od.imgSrc)}" alt="${escapeAttributeValue(label)}" onerror="window.builderFailedImages.add(this.src);this.parentElement.classList.add('bld-tile-none');" />` : ''}</div><div class="bld-tile-body"><span class="bld-tile-title">${escapeHtml(label)}</span><span class="bld-tile-price">${escapeHtml(displayPrice)}</span></div></div>`; });
                const isNoneSel = !activeVal || isOffLikeValue(activeVal) || activeVal === '0';
                html += `<div class="bld-tile ${isNoneSel ? 'selected' : ''}" data-inline-attr="${encodeURIComponent(tkName)}" data-val-name="0" role="button"><div class="bld-tile-img bld-tile-none"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="1.5"><circle cx="12" cy="12" r="9"></circle><line x1="5.5" y1="5.5" x2="18.5" y2="18.5"></line></svg></div><div class="bld-tile-body"><span class="bld-tile-title">None</span><span class="bld-tile-price"></span></div></div></div>`;
              }
              continue;
            }
            const forceToggle = (window.BUILDER_MUTEX_PAIRS || {})[tkName] !== undefined;
            const meaningfulOptions = validOptions.filter(v => !isOffLikeValue(v.name) && !['no','none'].includes(String(v.name).toLowerCase()));
            const isBinary = meaningfulOptions.length === 1 || forceToggle;
            if (isBinary) {
              const onlyOpt = meaningfulOptions[0] || null;
              let od;
              const ownSkuKey = (window.BUILDER_ATTR_SKU_KEY || {})[tkName];
              const ownSku = ownSkuKey ? status?.skus?.[ownSkuKey] : null;
              const ownShopifyData = ownSku && ownSku !== 'N/A' && partsMap[ownSku] ? partsMap[ownSku] : null;

              // For mutex attrs (J-Hooks): ALWAYS resolve from own fixed SKU first.
              // Never call resolveOptionData for mutex attrs — matchingPropagatedData
              // on the option object leaks wrong SKUs (e.g. pin SKUs) on production.
              const isMutexAttr = (window.BUILDER_MUTEX_PAIRS || {})[tkName] !== undefined;
              const fixedSku = (window.BUILDER_ATTR_FIXED_SKUS || {})[tkName];
              const fixedData = fixedSku && partsMap[fixedSku] ? partsMap[fixedSku] : null;

              if (ownShopifyData) {
                // status.skus has the specific key — use it (most accurate)
                let imgSrc = PLACEHOLDER_IMAGE;
                if (ownShopifyData.image) imgSrc = ownShopifyData.image.startsWith('//') ? `https:${ownShopifyData.image}` : ownShopifyData.image;
                od = { imgSrc, priceText: formatMoney(ownShopifyData.price || 0), skuText: `SKU ${ownSku}`, shopifyData: ownShopifyData };
              } else if (isMutexAttr && fixedData) {
                // Mutex attr without status.skus — use hardcoded fixed SKU.
                // This is the correct path on production when SKU_J-Hooks_Type4
                // is missing from status.skus. Never falls through to resolveOptionData.
                let imgSrc = PLACEHOLDER_IMAGE;
                if (fixedData.image) imgSrc = fixedData.image.startsWith('//') ? `https:${fixedData.image}` : fixedData.image;
                od = { imgSrc, priceText: fixedData.price ? formatMoney(fixedData.price) : '', skuText: `SKU ${fixedSku}`, shopifyData: fixedData };
              } else if (onlyOpt && attr && !isMutexAttr) {
                // Non-mutex binary attrs (Smith Machine, Spotter Arms) — safe to use resolveOptionData
                od = resolveOptionData(attr, onlyOpt, status);
              } else {
                // Final fallback — fixed SKU or placeholder
                let imgSrc = PLACEHOLDER_IMAGE;
                if (fixedData?.image) imgSrc = fixedData.image.startsWith('//') ? `https:${fixedData.image}` : fixedData.image;
                od = { imgSrc, priceText: fixedData?.price ? formatMoney(fixedData.price) : '', skuText: fixedSku ? `SKU ${fixedSku}` : '' };
              }
              const hasImg = od.imgSrc && od.imgSrc !== PLACEHOLDER_IMAGE && !window.builderFailedImages.has(od.imgSrc);
              const mutexPartner = (window.BUILDER_MUTEX_PAIRS || {})[tkName];
              const partnerIsOn = !!(mutexPartner && window.localUIState[mutexPartner] && !isOffLikeValue(window.localUIState[mutexPartner]));
              const isBlockedByMutex = partnerIsOn && !isSelected;
              const unavailReason = getAttrUnavailableReason(tkName, status);
              const isUnavail = !!unavailReason && !isSelected;
              html += `<div class="bld-row bld-toggle-row ${isSelected ? 'active' : ''} ${isBlockedByMutex ? 'bld-mutex-blocked' : ''} ${isUnavail ? 'bld-row-disabled' : ''}" ${isUnavail ? `data-tooltip="${escapeHtml(unavailReason)}"` : `data-binary-attr="${encodeURIComponent(tkName)}"`} role="button"><div class="bld-row-left">${hasImg ? `<div class="bld-thumb"><img src="${escapeAttributeValue(od.imgSrc)}" alt="${escapeAttributeValue(uiTitle)}" onerror="window.builderFailedImages.add(this.src);this.parentElement.style.display='none';" /></div>` : ''}<div class="bld-row-info"><div class="bld-row-title">${escapeHtml(uiTitle)}</div>${unavailReason ? `<div class="bld-row-unavail">${escapeHtml(unavailReason)}</div>` : ''}</div></div>${od.priceText ? `<div class="bld-row-price">${escapeHtml(od.priceText)}</div>` : ''}</div>`;
              continue;
            }
            const isExpanded = viewState.expandedInlineAttr === tkName;
            html += `<div class="bld-row bld-accordion-row ${isSelected ? 'active' : ''} ${isExpanded ? 'expanded' : ''}" data-inline-attr="${encodeURIComponent(tkName)}" role="button"><div class="bld-row-title">${escapeHtml(uiTitle)}${isSelected ? `<span class="bld-row-sub">${escapeHtml(formatName(window.translateTKName(activeVal)))}</span>` : ''}</div><div style="display:flex;align-items:center;gap:6px;">${isSelected ? `<div class="bld-x-btn" data-clear-attr="${encodeURIComponent(tkName)}" role="button"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></div>` : ''}<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">${isExpanded ? `<polyline points="18 15 12 9 6 15"></polyline>` : `<polyline points="6 9 12 15 18 9"></polyline>`}</svg></div></div>`;
            if (isExpanded) {
              html += `<div class="bld-cards-wrap">`; nonOffOptions.forEach(val => { const od = resolveOptionData(attr, val, status); const hasImg = od.imgSrc && od.imgSrc !== PLACEHOLDER_IMAGE && !window.builderFailedImages.has(od.imgSrc); const label = formatName(window.translateTKName(val.displayName || val.name || '')); const isSel = String(activeVal) === String(val.name); html += `<div class="bld-card ${isSel ? 'selected' : ''} ${!hasImg ? 'no-img' : ''}" data-inline-attr="${encodeURIComponent(tkName)}" data-val-name="${escapeHtml(String(val.name || ''))}" role="button">${hasImg ? `<div class="bld-card-img"><img src="${escapeAttributeValue(od.imgSrc)}" alt="${escapeAttributeValue(label)}" onerror="window.builderFailedImages.add(this.src);this.closest('.bld-card').classList.add('no-img');this.parentElement.style.display='none';" /></div>` : ''}<div class="bld-card-details"><div class="bld-card-title">${escapeHtml(label)}</div>${od.skuText ? `<div class="bld-card-sku">${escapeHtml(od.skuText)}</div>` : ''}</div><div class="bld-card-price">${escapeHtml(od.priceText)}</div></div>`; }); html += `</div>`;
            }
          }
        }
        const notes = (window.BUILDER_SECTION_NOTES || {})[sectionName] || [];
        const activeNotes = notes.filter(n => { try { return n.condition(window.localUIState || {}); } catch(e) { return false; } });
        const mutexPairsMap = window.BUILDER_MUTEX_PAIRS || {};
        const allAttrsInSection = Object.values(subsections).flat();
        const addedMutexNotes = new Set();
        allAttrsInSection.forEach(tkName => { const partner = mutexPairsMap[tkName]; if (!partner) return; const tkOn = !!(window.localUIState[tkName] && !isOffLikeValue(window.localUIState[tkName])); const partnerOn = !!(window.localUIState[partner] && !isOffLikeValue(window.localUIState[partner])); const pairKey = [tkName, partner].sort().join('|'); if (!addedMutexNotes.has(pairKey) && (tkOn || partnerOn)) { addedMutexNotes.add(pairKey); const selName = tkOn ? formatName(window.translateTKName(tkName)) : formatName(window.translateTKName(partner)); const blkName = tkOn ? formatName(window.translateTKName(partner)) : formatName(window.translateTKName(tkName)); activeNotes.push({ text: `${selName} is selected — Clicking ${blkName} will automatically swap.` }); } });
        if (activeNotes.length > 0) { html += `<div class="bld-section-notes">${activeNotes.map(n => `<div class="bld-section-note">${escapeHtml(n.text)}</div>`).join('')}</div>`; }
        html += `</div>`;
      }
      html += `</div>`;
    }
    html += `<button id="save-config-btn" class="builder-btn-outline" type="button" style="margin-top:1rem;"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>Save Configuration</button>`;
    root.innerHTML = html;

    root.querySelectorAll('.bld-section-head').forEach(el => {
      el.addEventListener('click', (e) => {
        if (e.target.closest('.bld-clear-section')) return;
        const sec = el.dataset.section; const isCurrentlyOpen = viewState.expandedSections.has(sec);
        if (isCurrentlyOpen) { const body = el.closest('.bld-section')?.querySelector('.bld-section-body'); if (body) { body.style.overflow = 'hidden'; body.style.height = body.scrollHeight + 'px'; requestAnimationFrame(() => { body.style.transition = 'height 0.32s cubic-bezier(.4,0,.2,1), opacity 0.25s ease'; body.style.height = '0px'; body.style.opacity = '0'; setTimeout(() => { viewState.expandedSections.delete(sec); renderUI(status); }, 320); }); } else { viewState.expandedSections.delete(sec); renderUI(status); } }
        else { viewState.expandedSections.clear(); viewState.expandedSections.add(sec); viewState.expandedInlineAttr = null; renderUI(status); const newBody = root.querySelector(`.bld-section[data-section="${CSS.escape(sec)}"] .bld-section-body`); if (newBody) { const fullH = newBody.scrollHeight; newBody.style.overflow = 'hidden'; newBody.style.height = '0px'; newBody.style.opacity = '0'; newBody.style.transition = 'none'; requestAnimationFrame(() => { requestAnimationFrame(() => { newBody.style.transition = 'height 0.35s cubic-bezier(.4,0,.2,1), opacity 0.28s ease'; newBody.style.height = fullH + 'px'; newBody.style.opacity = '1'; setTimeout(() => { newBody.style.height = ''; newBody.style.overflow = ''; newBody.style.transition = ''; newBody.style.opacity = ''; }, 360); }); }); } }
      });
    });
    root.querySelectorAll('.bld-clear-section').forEach(btn => { btn.addEventListener('click', async e => { e.stopPropagation(); const secName = btn.dataset.section; const subsections = window.BUILDER_SECTION_MAP[secName] || {}; const allAttrNames = []; for (const attrNames of Object.values(subsections)) for (const tk of attrNames) allAttrNames.push(tk); allAttrNames.forEach(tk => { window.localUIState[tk] = null;
        // Also clear position overrides for this section
        if (window._builderPositionOverride) delete window._builderPositionOverride[tk];
      }); const priceEl = document.getElementById('builder-total-price'); if (priceEl) priceEl.style.opacity = '0.5'; for (const tk of [...allAttrNames].reverse()) { const empty = getEmptyPayload(tk, attributes); if (!empty && empty !== 0) continue; try { await updateThreekit(tk, empty); await new Promise(r => setTimeout(r, 80)); } catch(err) { console.warn(`Clear ${tk}:`, err.message); } } viewState.expandedInlineAttr = null; await refreshBuilderState(`clear-section:${secName}`); }); });
    root.querySelectorAll('.bld-swatch[data-color-attr]').forEach(sw => { sw.addEventListener('click', async () => { const tkName = decodeURIComponent(sw.dataset.colorAttr || ''); const selVal = sw.dataset.valName || ''; const a2 = attributes.find(a => a.name === tkName); const sObj = (a2?.values || []).find(v => String(v.name) === String(selVal)); window.localUIState[tkName] = selVal; const priceEl = document.getElementById('builder-total-price'); if (priceEl) priceEl.style.opacity = '0.5'; await updateThreekit(tkName, sObj ? getThreekitPayloadValue(sObj) : selVal); await refreshBuilderState(`color:${tkName}`, { forceMainView: false }); }); });
    root.querySelectorAll('.bld-chip[data-chip-attr]').forEach(chip => { chip.addEventListener('click', async () => { const tkName = decodeURIComponent(chip.dataset.chipAttr || ''); const selVal = chip.dataset.valName || ''; const a2 = attributes.find(a => a.name === tkName); const sObj = (a2?.values || []).find(v => String(v.name) === String(selVal)); const prevChipVal = window.localUIState[tkName]; window.localUIState[tkName] = selVal;
        // Weight stack position: save to protected override so syncLocalUiState
        // cannot clobber the user's Right-side choice with Threekit's Left default.
        if (tkName === 'Weight_Stack1_Position' || tkName === 'Weight_Stack2_Position') {
          window._builderPositionOverride = window._builderPositionOverride || {};
          window._builderPositionOverride[tkName] = selVal;
          // Clear the OTHER position override so it doesn't shadow the new selection.
          // e.g. user picks Left then Right: Stack1 override must be cleared so
          // the resolver reads Stack2 override and routes it correctly.
          const other = tkName === 'Weight_Stack1_Position' ? 'Weight_Stack2_Position' : 'Weight_Stack1_Position';
          delete window._builderPositionOverride[other];
          console.log(`[pos-override] ${tkName} = ${selVal} (cleared ${other})`);
        }
        const priceEl = document.getElementById('builder-total-price'); if (priceEl) priceEl.style.opacity = '0.5';
        // With 1 stack only one side can be positioned: when switching Left <-> Right,
        // clear the other side first (only if Threekit offers a real "None" option for it).
        if ((tkName === 'Weight_Stack1_Position' || tkName === 'Weight_Stack2_Position') && String(window.localUIState.Weight_Stack_Amount) === '1') {
          const otherName = tkName === 'Weight_Stack1_Position' ? 'Weight_Stack2_Position' : 'Weight_Stack1_Position';
          const otherAttr = attributes.find(a => a.name === otherName);
          const otherNone = (otherAttr?.values || []).find(v => isOffLikeValue(v.name) || isOffLikeValue(v.value));
          if (otherNone && window.localUIState[otherName] && !isOffLikeValue(window.localUIState[otherName])) {
            window.localUIState[otherName] = null;
            try { await updateThreekit(otherName, getThreekitPayloadValue(otherNone)); } catch(err) { console.warn('Clear other stack side:', err.message); }
          }
        }
        await updateThreekit(tkName, sObj ? getThreekitPayloadValue(sObj) : selVal);
        if (tkName === 'Weight_Stack_Amount') {
          if (String(selVal) === '1') {
            // 1 stack: pick a default position so the customer is never left without one.
            await ensureDefaultSingleStackPosition();
          } else if (String(prevChipVal) === '1' && window._builderPositionOverride) {
            // Leaving single-stack mode: drop the single-stack Right override so it cannot shadow Stack 2.
            delete window._builderPositionOverride['Weight_Stack2_Position'];
          }
        }
        await refreshBuilderState(`chip:${tkName}`, { forceMainView: false }); }); });

    // Binary toggle — mutex auto-swap: 300ms settle + single fresh status read
    root.querySelectorAll('.bld-toggle-row[data-binary-attr]').forEach(row => {
      row.addEventListener('click', async () => {
        const tkName = decodeURIComponent(row.dataset.binaryAttr || '');
        const isOn = !!(window.localUIState[tkName] && !isOffLikeValue(window.localUIState[tkName]));
        const a2 = attributes.find(a => a.name === tkName);
        const meaningfulOpts = (a2?.values || []).filter(v => v.visible !== false && v.enabled !== false && !isOffLikeValue(v.name) && !['no','none'].includes(String(v.name).toLowerCase()));
        const partner = (window.BUILDER_MUTEX_PAIRS || {})[tkName];
        const partnerIsOn = !!(partner && window.localUIState[partner] && !isOffLikeValue(window.localUIState[partner]));

        if (isOn) {
          // Deselect — turn off
          window.localUIState[tkName] = null;
          renderUI(status); // immediate UI feedback
          try { await updateThreekit(tkName, getEmptyPayload(tkName, attributes)); } catch(err) { console.warn('Toggle off:', err.message); }
        } else {
          // Select — clear mutex partner first if needed
          if (partner && partnerIsOn) {
            window.localUIState[partner] = null;
            // Get fresh attributes so getEmptyPayload finds partner's "No" option
            let freshAttrsForClear = attributes;
            try { let sf = extension.getStatus(); if (sf instanceof Promise) sf = await sf; if (sf?.validAttributesAndTheirValues) freshAttrsForClear = sf.validAttributesAndTheirValues; } catch(e) {}
            const clearPayload = getEmptyPayload(partner, freshAttrsForClear);
            console.log(`[mutex] Clearing ${partner}, payload:`, JSON.stringify(clearPayload));
            try { await updateThreekit(partner, clearPayload); } catch(err) { console.warn('Mutex clear:', err.message); }
            await new Promise(r => setTimeout(r, 300));
          }

          // Get fresh options for the attr being activated
          let freshOpts = meaningfulOpts;
          try {
            let s = extension.getStatus(); if (s instanceof Promise) s = await s;
            if (s?.validAttributesAndTheirValues) {
              attributes.splice(0, attributes.length, ...(s.validAttributesAndTheirValues || []));
              const a2Fresh = attributes.find(a => a.name === tkName);
              freshOpts = (a2Fresh?.values || []).filter(v => v.visible !== false && v.enabled !== false && !isOffLikeValue(v.name) && !['no','none'].includes(String(v.name).toLowerCase()));
            }
          } catch(e) {}

          const onlyOpt = freshOpts[0] || meaningfulOpts[0];
          if (onlyOpt) {
            // Set localUIState and render BEFORE the async Threekit call
            // so the toggle shows as active immediately
            window.localUIState[tkName] = String(onlyOpt.name);
            renderUI(status); // immediate UI feedback — toggle turns on now
            const priceEl = document.getElementById('builder-total-price');
            if (priceEl) priceEl.style.opacity = '0.5';
            try { await updateThreekit(tkName, getThreekitPayloadValue(onlyOpt)); } catch(err) { console.warn('Toggle on:', err.message); }
          }
        }

        viewState.expandedInlineAttr = null;
        await refreshBuilderState(`toggle:${tkName}`, { forceMainView: false });
      });
    });

    root.querySelectorAll('.bld-accordion-row[data-inline-attr]').forEach(row => { row.addEventListener('click', async (e) => { if (e.target.closest('.bld-x-btn')) return; const tkName = decodeURIComponent(row.dataset.inlineAttr || ''); viewState.expandedInlineAttr = (viewState.expandedInlineAttr === tkName) ? null : tkName; renderUI(status); }); });
    root.querySelectorAll('.bld-card[data-inline-attr], .bld-tile[data-inline-attr]').forEach(card => { card.addEventListener('click', async () => { const tkName = decodeURIComponent(card.dataset.inlineAttr || ''); const selVal = card.dataset.valName || ''; const a2 = attributes.find(a => a.name === tkName); const priceEl = document.getElementById('builder-total-price'); if (priceEl) priceEl.style.opacity = '0.5'; if (selVal === '0' || selVal === '' || isOffLikeValue(selVal)) { window.localUIState[tkName] = null; try { await updateThreekit(tkName, getEmptyPayload(tkName, attributes)); } catch(err) { console.warn('Tile clear:', err.message); } } else { const sObj = (a2?.values || []).find(v => String(v.name) === String(selVal)); window.localUIState[tkName] = selVal; await updateThreekit(tkName, sObj ? getThreekitPayloadValue(sObj) : selVal); } await refreshBuilderState(`card:${tkName}`, { forceMainView: false }); }); });
    root.querySelectorAll('.bld-x-btn[data-clear-attr]').forEach(btn => { btn.addEventListener('click', async e => { e.stopPropagation(); const tkName = decodeURIComponent(btn.dataset.clearAttr || ''); window.localUIState[tkName] = null;
        // Clear position override if applicable
        if (window._builderPositionOverride) delete window._builderPositionOverride[tkName];
        try { await updateThreekit(tkName, getEmptyPayload(tkName, attributes)); } catch(err) { console.warn('Clear attr:', err.message); } await refreshBuilderState(`clear:${tkName}`, { forceMainView: false }); }); });
    const saveBtn = document.getElementById('save-config-btn');
    if (saveBtn) { saveBtn.addEventListener('click', async () => { saveBtn.textContent = 'Generating Link...'; saveBtn.disabled = true; try { const params = new URLSearchParams(); Object.entries(window.localUIState).forEach(([key, value]) => { if (value !== null && value !== undefined && value !== '' && !isOffLikeValue(String(value))) params.set(key, value); }); const shareUrl = window.location.origin + window.location.pathname + (params.toString() ? `?${params.toString()}` : ''); let copied = false; if (navigator.clipboard && window.isSecureContext) { try { await navigator.clipboard.writeText(shareUrl); copied = true; } catch(e) {} } if (!copied) { const ta = document.createElement('textarea'); ta.value = shareUrl; ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;'; document.body.appendChild(ta); ta.focus(); ta.select(); try { document.execCommand('copy'); copied = true; } catch(e) {} document.body.removeChild(ta); } saveBtn.textContent = copied ? 'Link Copied!' : 'Link Ready'; if (!copied) window.prompt('Copy this link:', shareUrl); setTimeout(() => { saveBtn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg> Save Configuration`; saveBtn.disabled = false; }, 2500); } catch(e) { saveBtn.textContent = 'Error'; saveBtn.disabled = false; } }); }
  }


  function resolveOptionData(attr, val, status) {
    // For amount attrs (Weight_Pins_20cm_Amount, Weight_Pins_30cm_Amount):
    // getAddonSkusForOption blocks pin SKUs to prevent BOM leakage from other attrs.
    // So we look up the per-unit pin SKU directly from BUILDER_ATTR_FIXED_SKUS here,
    // before calling getOptionSkus. This gives tiles their correct image and price.
    const PIN_UNIT_SKUS = window.BUILDER_PIN_UNIT_SKUS || {};
    const directPinSku = PIN_UNIT_SKUS[attr.name];
    if (directPinSku && partsMap[directPinSku]) {
      const pinData = partsMap[directPinSku];
      const qty = parseInt(String(val.name), 10) || 1;
      let imgSrc = PLACEHOLDER_IMAGE;
      if (pinData.image) imgSrc = pinData.image.startsWith('//') ? `https:${pinData.image}` : pinData.image;
      return {
        resolvedSkus: [directPinSku],
        shopifyData: pinData,
        skuText: `SKU ${directPinSku}`,
        priceText: pinData.price ? formatMoney(Number(pinData.price) * qty) : '',
        imgSrc
      };
    }

    let resolvedSkus = getOptionSkus(attr, val, status);

    // For binary (Yes/No) attributes, getOptionSkus returns empty because
    // the SKU lives in status.skus keyed by a different name (e.g. SKU_J-Hooks),
    // not in the option object's matchingPropagatedData.
    // Read it from status.skus when the option value is "Yes" and skus is empty.
        // 3-tier SKU fallback for binary attrs (J-Hooks, Smith Machine etc.)
    if (resolvedSkus.length === 0) {
      const valStr = String(val.name).toLowerCase();
      const isPositiveVal = valStr === 'yes' || (parseInt(valStr, 10) > 0 && !isNaN(parseInt(valStr, 10)));
      if (isPositiveVal) {
        const specificKey = (window.BUILDER_ATTR_SKU_KEY || {})[attr.name];
        if (specificKey) { const sku = status?.skus?.[specificKey]; if (sku && sku !== 'N/A' && partsMap[sku]) resolvedSkus = [sku]; }
        if (resolvedSkus.length === 0) { const dk = `SKU_${attr.name.replace(/-/g, '_')}`; const sku = status?.skus?.[dk]; if (sku && sku !== 'N/A' && partsMap[sku]) resolvedSkus = [sku]; }
        if (resolvedSkus.length === 0 && (attr.name === 'J-Hooks_Type1' || attr.name === 'J-Hooks_Type4')) { const sku = status?.skus?.['SKU_J-Hooks']; if (sku && sku !== 'N/A' && partsMap[sku]) resolvedSkus = [sku]; }
      }
    }

    const primarySku = resolvedSkus[0] || '';
    const shopifyData = primarySku ? partsMap[primarySku] || null : null;

    let priceText = '';
    if (shopifyData && shopifyData.price) {
      priceText = formatMoney(shopifyData.price);
    }

    const skuText = primarySku ? `SKU ${primarySku}` : '';

    let imgSrc = PLACEHOLDER_IMAGE;
    if (shopifyData && shopifyData.image) {
      imgSrc = shopifyData.image.startsWith('//') ? `https:${shopifyData.image}` : shopifyData.image;
    } else if (val.assetId && val.assetId !== 'N/A') {
      const tkDomain = window.builderProduct.scriptUrl
        ? new URL(window.builderProduct.scriptUrl).origin
        : 'https://admin-fts.threekit.com';
      imgSrc = `${tkDomain}/api/assets/${val.assetId}/thumbnail?bearer_token=${THREEKIT_AUTH_TOKEN}`;
    }

    return { resolvedSkus, shopifyData, skuText, priceText, imgSrc };
  }

  function extractSkusShallow(val) {
    const found = new Set();
    if (!val || typeof val !== 'object') return found;

    Object.entries(val).forEach(([key, value]) => {
      if (key && String(key).toLowerCase().includes('sku') && typeof value !== 'object') {
        splitSkuValue(value).forEach(sku => found.add(sku));
      }
    });

    if (val.metadata && typeof val.metadata === 'object') {
      Object.entries(val.metadata).forEach(([key, value]) => {
        if (key && String(key).toLowerCase().includes('sku')) {
          splitSkuValue(value).forEach(sku => found.add(sku));
        }
      });
    }

    if (val.matchingPropagatedData && typeof val.matchingPropagatedData === 'object') {
      Object.entries(val.matchingPropagatedData).forEach(([key, value]) => {
        if (key && String(key).toLowerCase().includes('sku')) {
          splitSkuValue(value).forEach(sku => found.add(sku));
        }
      });
    }

    return found;
  }

  function getAddonSkusForOption(attr, val) {
    const skuSet = new Set();
    extractSkusShallow(val).forEach(sku => skuSet.add(sku));
    const mappedSku = getSkuFromBuilderMappings(attr, val);
    if (mappedSku) skuSet.add(String(mappedSku).trim());

    // Block SKUs that must only enter the BOM via explicit tile/amount selection.
    // On production, Threekit's matchingPropagatedData leaks pin and weight stack
    // SKUs onto unrelated option objects (e.g. J-Hooks Type 4 carries pin SKUs).
    // Weight stack SKUs are handled by resolveWeightStackSkus.
    // Pin SKUs are handled by the tile grid selection (card/tile click handler).
    const WEIGHT_STACK_SKU_MAP = window.WEIGHT_STACK_SKU_MAP || {};
    const blockedSkus = new Set([
      // All weight stack composite SKUs
      ...Object.values(WEIGHT_STACK_SKU_MAP),
      // Weight pin SKUs — must come from explicit tile selection only
      '555-1451', // 20cm pin
      '555-1450', // 30cm pin
      // Weight stack placeholder
      '555-3381',
    ]);

    return Array.from(skuSet).filter(sku => partsMap[sku] && !blockedSkus.has(sku));
  }

  // ─── WEIGHT STACK COMPOSITE SKU RESOLVER ─────────────────────────────────
  //
  // Threekit outputs a placeholder SKU (555-3381) for weight stacks because the
  // data table doesn't map the 3-way composite (Side × Crossbar × Position).
  // We resolve the correct SKU dynamically from partsMap using:
  //   - Crossbar width  (from the Crossbar attribute: "42", "78", "108")
  //   - Position code   (DCL2 = Links/Pos2, DCR3 = Rechts/Pos3, etc.)
  //
  // Position code format: DCL = Links (Left), DCR = Rechts (Right), digit = position number
  // partsMap title format: "R8 Bradley | 95kg Kabelzug Anbau | Links | 108cm Crossbar | Position 2 - ..."
  //
  // ─── WEIGHT STACK COMPOSITE SKU RESOLVER ─────────────────────────────────
  // Maps are defined in builder-map.js as window.WEIGHT_STACK_SKU_MAP
  // and window.CROSSBAR_SKU_TO_WIDTH — edit those files to add new variants.
  function resolveWeightStackSkus(status, activeSkus) {

    // Read maps from builder-map.js (language-independent, SKU-based)
    const WEIGHT_STACK_SKU_MAP = window.WEIGHT_STACK_SKU_MAP || {};
    const CROSSBAR_SKU_TO_WIDTH = window.CROSSBAR_SKU_TO_WIDTH || {};

    // ── 1. Check weight system is active ───────────────────────────────────
    const weightSystemAttr = status.validAttributesAndTheirValues?.find(a => a.name === 'Weight_System');
    const weightSystemActive =
      weightSystemAttr?.values?.find(v => v.selected)?.name ||
      status.attributes?.Weight_System ||
      window.localUIState?.Weight_System;

    if (!weightSystemActive || isOffLikeValue(String(weightSystemActive))) return;

    // ── 2. Read crossbar width ─────────────────────────────────────────────
    // Source A: attribute selected flag or localUIState
    const crossbarAttr = status.validAttributesAndTheirValues?.find(a => a.name === 'Crossbar');
    let crossbarWidth =
      crossbarAttr?.values?.find(v => v.selected)?.name ||
      status.attributes?.Crossbar ||
      window.localUIState?.Crossbar;

    // Source B: derive from active crossbar SKU via CROSSBAR_SKU_TO_WIDTH
    // Needed when syncLocalUiState nulled out localUIState.Crossbar because
    // prod DDC never sets v.selected on numeric/derived attributes
    if (!crossbarWidth || isOffLikeValue(String(crossbarWidth))) {
      const crossbarSku =
        status.skus?.SKU_C_Crossbar_DCL ||
        status.skus?.SKU_D_Crossbar_DCR;

      if (crossbarSku) {
        crossbarWidth = CROSSBAR_SKU_TO_WIDTH[String(crossbarSku)] || null;
        if (crossbarWidth) {
          console.log(`Crossbar width from SKU lookup: ${crossbarSku} → ${crossbarWidth}cm`);
        }
      }
    }

    if (!crossbarWidth || isOffLikeValue(String(crossbarWidth))) {
      console.warn('Weight Stack resolver: could not determine crossbar width — skipping');
      return;
    }

    // ── 3. Read stack amount ───────────────────────────────────────────────
    const stackAmountAttr = status.validAttributesAndTheirValues?.find(a => a.name === 'Weight_Stack_Amount');
    const stackAmount = parseInt(
      stackAmountAttr?.values?.find(v => v.selected)?.name ||
      status.attributes?.Weight_Stack_Amount ||
      window.localUIState?.Weight_Stack_Amount || '0',
      10
    );

    if (!stackAmount || stackAmount < 1) return;

    // ── 4. Strip ALL weight stack SKUs from the BOM ───────────────────────
    // Threekit may output any weight stack SKU as a "best guess" from its
    // data table — it could be the placeholder (555-3381) OR a real but
    // wrong composite SKU (e.g. 555-3379 for a different position/width).
    // We always remove every weight stack SKU from the BOM and let the
    // resolver below add exactly the right one(s).
    const allWeightStackSkus = new Set([
      WEIGHT_STACK_PLACEHOLDER_SKU,
      ...Object.values(WEIGHT_STACK_SKU_MAP)
    ]);

    let i = activeSkus.length - 1;
    while (i >= 0) {
      if (allWeightStackSkus.has(activeSkus[i])) activeSkus.splice(i, 1);
      i--;
    }

    // ── 5. Parse position code ────────────────────────────────────────────
    // DCL2/DCR2 = pos1, DCL3/DCR3 = pos2, DCL4/DCR4 = pos3
    function parsePositionCode(code) {
      if (!code) return null;
      const upper = String(code).toUpperCase().trim();
      const side = upper.startsWith('DCL') ? 'left' :
                  upper.startsWith('DCR') ? 'right' : null;
      const posMatch = upper.match(/\d+/);
      const position = posMatch ? String(parseInt(posMatch[0], 10) - 1) : null;
      return (side && position && parseInt(position) > 0) ? { side, position } : null;
    }

    // ── 6. Look up composite SKU from map ─────────────────────────────────
    function findCompositeSku(side, width, position) {
      const key = `${side}_${width}_${position}`;
      const sku = WEIGHT_STACK_SKU_MAP[key] || null;
      if (!sku) return null;
      // Verify it exists in partsMap (added to configurator-parts collection)
      return partsMap[sku] ? sku : null;
    }

    // ── 7. Resolve Stack 1 ────────────────────────────────────────────────
    const pos1Attr = status.validAttributesAndTheirValues?.find(a => a.name === 'Weight_Stack1_Position');
    // When amount=1, the UI shows only one position row (Weight_Stack1_Position).
    // But before the skip-condition fix, the chip was rendered under Weight_Stack2_Position,
    // so the override may have been stored under Stack2 key. Check both.
    const overrideForStack1 =
      (window._builderPositionOverride || {})['Weight_Stack1_Position'] ||
      (stackAmount === 1 ? (window._builderPositionOverride || {})['Weight_Stack2_Position'] : null);

    const pos1Code =
      overrideForStack1 ||
      window.localUIState?.Weight_Stack1_Position ||
      pos1Attr?.values?.find(v => v.selected)?.name ||
      status.attributes?.Weight_Stack1_Position;

    console.log(`[resolver] Stack1 pos1Code=${pos1Code} (override1=${(window._builderPositionOverride||{})['Weight_Stack1_Position']}, override2=${(window._builderPositionOverride||{})['Weight_Stack2_Position']}, localUI=${window.localUIState?.Weight_Stack1_Position}, tkSelected=${pos1Attr?.values?.find(v=>v.selected)?.name}, amount=${stackAmount})`);

    const effectivePos1 = (viewState.currentView === 'Weight_Stack1_Position' && viewState.tempSelection)
      ? viewState.tempSelection : pos1Code;

    const parsed1 = parsePositionCode(effectivePos1);
    if (parsed1) {
      const sku1 = findCompositeSku(parsed1.side, crossbarWidth, parsed1.position);
      if (sku1) {
        console.log(`✅ Weight Stack 1: ${effectivePos1} + ${crossbarWidth}cm → ${sku1}`);
        activeSkus.push(sku1);
      } else {
        console.warn(`❌ Weight Stack 1: no SKU for key ${parsed1.side}_${crossbarWidth}_${parsed1.position} — add to WEIGHT_STACK_SKU_MAP in builder-map.js`);
      }
    }

    // ── 8. Resolve Stack 2 (only if amount >= 2) ──────────────────────────
    if (stackAmount >= 2) {
      const pos2Attr = status.validAttributesAndTheirValues?.find(a => a.name === 'Weight_Stack2_Position');
      const pos2Code =
        (window._builderPositionOverride || {})['Weight_Stack2_Position'] ||
        window.localUIState?.Weight_Stack2_Position ||
        pos2Attr?.values?.find(v => v.selected)?.name ||
        status.attributes?.Weight_Stack2_Position;

      console.log(`[resolver] Stack2 pos2Code=${pos2Code} (override=${(window._builderPositionOverride||{})['Weight_Stack2_Position']})`);

      const effectivePos2 = (viewState.currentView === 'Weight_Stack2_Position' && viewState.tempSelection)
        ? viewState.tempSelection : pos2Code;

      const parsed2 = parsePositionCode(effectivePos2);
      if (parsed2) {
        const sku2 = findCompositeSku(parsed2.side, crossbarWidth, parsed2.position);
        if (sku2) {
          console.log(`✅ Weight Stack 2: ${effectivePos2} + ${crossbarWidth}cm → ${sku2}`);
          activeSkus.push(sku2);
        } else {
          console.warn(`❌ Weight Stack 2: no SKU for key ${parsed2.side}_${crossbarWidth}_${parsed2.position} — add to WEIGHT_STACK_SKU_MAP in builder-map.js`);
        }
      }
    }
  }
  // ─── END WEIGHT STACK RESOLVER ───────────────────────────────────────────


  function getSelectedVariants(status) {
    const selectedVariants = [];
    const variantsById = new Map();

    // 1. Get base SKUs from Threekit's data table (status.skus)
    const activeSkus = collectActiveStatusSkus(status);

    // 2. Run the weight stack composite SKU resolver.
    //    This strips the placeholder SKU (555-3381) and pushes the
    //    correct composite SKU(s) based on crossbar width + position codes.
    resolveWeightStackSkus(status, activeSkus);

    // 3. Mutex attr cleanup — strip both J-Hook SKUs, add back only the active one
    const fixedSkus = window.BUILDER_ATTR_FIXED_SKUS || {};
    const mutexAttrList = Object.keys(window.BUILDER_MUTEX_PAIRS || {});
    const processedMutexPairs = new Set();
    mutexAttrList.forEach(tkName => {
      const partner = (window.BUILDER_MUTEX_PAIRS || {})[tkName];
      const pairKey = [tkName, partner].sort().join('|');
      if (processedMutexPairs.has(pairKey)) return;
      processedMutexPairs.add(pairKey);
      const tkActive = !!(window.localUIState[tkName] && !isOffLikeValue(window.localUIState[tkName]));
      const partnerActive = !!(window.localUIState[partner] && !isOffLikeValue(window.localUIState[partner]));
      const skuA = fixedSkus[tkName]; const skuB = fixedSkus[partner];
      const removeAll = (sku) => { if (!sku) return; let i = activeSkus.indexOf(sku); while (i !== -1) { activeSkus.splice(i, 1); i = activeSkus.indexOf(sku); } };
      removeAll(skuA); removeAll(skuB);
      if (tkActive && skuA && partsMap[skuA]) activeSkus.push(skuA);
      else if (partnerActive && skuB && partsMap[skuB]) activeSkus.push(skuB);
    });

    // 4. Track BOM allowances to prevent double-counting structural parts
    //    that are already in activeSkus AND appear in addon metadata.
    const bomSkuAllowance = {};
    activeSkus.forEach(sku => {
      bomSkuAllowance[sku] = (bomSkuAllowance[sku] || 0) + 1;
    });

    // 5. Scan UI-mapped attributes for addon SKUs (colors, attachments etc.)
    //    Skip _Amount, _Position, AND mutex attrs — J-Hooks handled in step 3 above.
    //    Never fall back to status.attributes for mutex attrs — localUIState is the truth.
    const mutexAttrSet = new Set(Object.keys(window.BUILDER_MUTEX_PAIRS || {}));
    const attributes = status.validAttributesAndTheirValues || [];
    attributes.forEach(attr => {
      if (!isAttributeInUiMap(attr.name)) return;
      if (mutexAttrSet.has(attr.name)) return; // handled via localUIState in step 3

      const attrLower = attr.name.toLowerCase();
      if (attrLower.includes('_amount') ||
          attrLower.includes('_position') ||
          attrLower.includes('_pos_')) return;

      let selectedValName = window.localUIState[attr.name];

      // Use tempSelection for instant price preview while browsing Level 3
      if (viewState.currentView === attr.name && viewState.tempSelection) {
        selectedValName = viewState.tempSelection;
      }

      // Fall back to raw Threekit status if localUIState doesn't have it
      if (!selectedValName && status.attributes) {
        const configVal = status.attributes[attr.name];
        if (configVal) {
          selectedValName = typeof configVal === 'object' ? configVal.assetId : configVal;
        }
      }

      if (!selectedValName) return;

      const selectedOption = (attr.values || []).find(v =>
        String(v.name) === String(selectedValName) ||
        String(v.displayName) === String(selectedValName) ||
        String(v.assetId) === String(selectedValName)
      );

      if (selectedOption && !isOptionEffectivelyOff(selectedOption)) {
        const addonSkus = getAddonSkusForOption(attr, selectedOption);

        addonSkus.forEach(sku => {
          if (bomSkuAllowance[sku] > 0) {
            bomSkuAllowance[sku]--;
          } else {
            activeSkus.push(sku);
          }
        });
      }
    });

    // 6. Aggregate all SKUs into variantsById (handles quantity counting)
    activeSkus.forEach(sku => addSkuToVariantSelection(sku, variantsById));

    // 7. Build the final array
    variantsById.forEach((entry, variantId) => {
      selectedVariants.push({
        id: Number(variantId),
        product_id: Number(entry.product_id),
        price: Number(entry.price),
        title: entry.title,
        sku: entry.sku,
        quantity: entry.quantity
      });
    });

    return selectedVariants;
  }

  function collectActiveStatusSkus(status) {
    const skus = status?.skus || {};
    const results = [];

    const jHookType1Active = !!(window.localUIState?.['J-Hooks_Type1'] && !isOffLikeValue(window.localUIState['J-Hooks_Type1']));
    const jHookType4Active = !!(window.localUIState?.['J-Hooks_Type4'] && !isOffLikeValue(window.localUIState['J-Hooks_Type4']));
    const skipKeys = new Set(['SKU_J-Hooks', ...(!jHookType1Active ? ['SKU_J-Hooks_Type1'] : []), ...(!jHookType4Active ? ['SKU_J-Hooks_Type4'] : [])]);

    // Pin SKUs must only enter the BOM when the user explicitly selects a quantity
    // via the tile grid. Threekit includes them in status.skus regardless of whether
    // pins are selected, causing them to leak into the BOM on production.
    const pinSkus = new Set(['555-1451', '555-1450']);
    const pins20Active = !!(window.localUIState?.['Weight_Pins_20cm_Amount'] && !isOffLikeValue(window.localUIState['Weight_Pins_20cm_Amount']));
    const pins30Active = !!(window.localUIState?.['Weight_Pins_30cm_Amount'] && !isOffLikeValue(window.localUIState['Weight_Pins_30cm_Amount']));

    Object.entries(skus).forEach(([key, rawValue]) => {
      if (skipKeys.has(key)) return;
      if (!rawValue) return;

      if (Array.isArray(rawValue)) {
        rawValue.forEach(item => {
          splitSkuValue(item).forEach(sku => {
            if (pinSkus.has(sku) && sku === '555-1451' && !pins20Active) return;
            if (pinSkus.has(sku) && sku === '555-1450' && !pins30Active) return;
            results.push(sku);
          });
        });
        return;
      }

      splitSkuValue(rawValue).forEach(sku => {
        if (sku === '555-1451' && !pins20Active) return;
        if (sku === '555-1450' && !pins30Active) return;
        results.push(sku);
      });
    });

    return results;
  }

  function getOptionSkus(attr, val, status) {
    const skuSet = new Set();

    extractSkusShallow(val).forEach(sku => skuSet.add(sku));

    const mappedSku = getSkuFromBuilderMappings(attr, val);
    if (mappedSku) skuSet.add(String(mappedSku).trim());

    const selectedSkuFromStatus = getSkuFromStatus(attr, val, status);
    splitSkuValue(selectedSkuFromStatus).forEach(sku => skuSet.add(sku));

    return Array.from(skuSet).filter(sku => partsMap[sku]);
  }

  function getSkuFromBuilderMappings(attr, val) {
    const mappings = Array.isArray(window.builderMappings) ? window.builderMappings : [];
    if (!mappings.length) return '';

    const attrName = String(attr?.name || '');
    const valueName = String(val?.name || '');

    const matched = mappings.find(m =>
      String(m.threekit_attribute || '') === attrName &&
      String(m.threekit_value || '') === valueName
    );

    if (!matched) return '';

    return (
      matched.sku ||
      matched.variant_sku ||
      matched.shopify_sku ||
      matched.product_sku ||
      matched?.variant?.sku ||
      ''
    );
  }

  function getAttributeSelectedValue(attrName) {
    return window.localUIState[attrName];
  }

  function addSkuToVariantSelection(sku, variantsById) {
    if (!sku || !partsMap[sku]) return;
    const variant = partsMap[sku];
    const variantId = Number(variant.id);
    if (!variantId) return;
    if (variantsById.has(variantId)) { variantsById.get(variantId).quantity += 1; return; }
    variantsById.set(variantId, { ...variant, quantity: 1 });
  }

  function isSkuUnavailable(sku) {
    if (!sku) return false;
    const v = partsMap[sku];
    if (!v) return true;
    if (v.available === false) return true;
    if (typeof v.inventory_quantity === 'number' && v.inventory_quantity <= 0 && v.inventory_policy !== 'continue') return true;
    return false;
  }

  function getAttrUnavailableReason(tkName, status) {
    const fixedSku = (window.BUILDER_ATTR_FIXED_SKUS || {})[tkName];
    const skuKey = (window.BUILDER_ATTR_SKU_KEY || {})[tkName];
    const statusSku = skuKey ? status?.skus?.[skuKey] : null;
    const sku = (statusSku && statusSku !== 'N/A') ? statusSku : fixedSku;
    if (!sku) return null;
    if (!partsMap[sku]) return 'Currently unavailable';
    if (isSkuUnavailable(sku)) return 'Currently out of stock';
    return null;
  }

  function getSkuFromStatus(attr, val, status) {
    const currentConfig = status?.attributes || {};
    const activeSkus = status?.skus || {};
    const currentAttrValue = currentConfig?.[attr?.name];
    const uiSelectedValue = getAttributeSelectedValue(attr?.name);

    const isSelected =
      isOptionSelectedByConfig(val, currentAttrValue) ||
      String(val?.name) === String(uiSelectedValue) ||
      String(val?.displayName) === String(uiSelectedValue);

    if (!isSelected) return '';

    const byAttrName = activeSkus?.[attr?.name];
    if (byAttrName && String(byAttrName).trim()) {
      return String(byAttrName).trim();
    }

    const activeAttribute = (status?.validAttributesAndTheirValues || []).find(
      attribute => String(attribute?.name) === String(attr?.name)
    );

    if (activeAttribute) {
      const selectedOption = (activeAttribute.values || []).find(option =>
        isOptionSelectedByConfig(option, currentAttrValue)
      );

      const nestedSku = Array.from(extractSkusShallow(selectedOption))[0];
      if (nestedSku) return nestedSku;
    }

    return '';
  }

  function isOptionSelectedByConfig(option, configValue) {
    if (configValue === undefined || configValue === null || configValue === '') return false;

    if (typeof configValue === 'object' && configValue?.assetId) {
      return option?.assetId === configValue.assetId;
    }

    if (option?.value !== undefined && String(option.value) === String(configValue)) return true;
    if (option?.name !== undefined && String(option.name) === String(configValue)) return true;

    return false;
  }

  function isOptionEffectivelyOff(option) {
    if (!option) return true;
    
    if (option.name !== undefined && isOffLikeValue(option.name)) return true;
    if (option.value !== undefined && isOffLikeValue(option.value)) return true;
    if (option.displayName !== undefined && isOffLikeValue(option.displayName)) return true;
    
    return false;
  }

  function getThreekitPayloadValue(option) {
    if (option.assetId && option.assetId !== 'N/A') {
      return { assetId: option.assetId };
    }

    if (option.value !== undefined && option.value !== null) {
      return option.value;
    }

    return option.name;
  }

  function normalizeComparableValue(value) {
    return String(value ?? '').toLowerCase().trim();
  }

  function isZeroLikeValue(value) {
    if (value === undefined || value === null) return false;
    const normalized = normalizeComparableValue(value);
    return normalized === '0';
  }

  function isOffLikeValue(value) {
    if (value === undefined || value === null) return false;
    const normalized = normalizeComparableValue(value);
    return [
      '',
      '0',
      'no',
      'none',
      'off',
      'without',
      'n/a',
      'tkdatadrivenconfiguratortooldataattributeunavailable'
    ].includes(normalized);
  }

  function splitSkuValue(value) {
    if (value === undefined || value === null) return [];

    return String(value)
      .split(',')
      .map(part => part.trim())
      .filter(part => part && part !== 'N/A');
  }

  function updatePrice(status) {
    currentCartPayload = getSelectedVariants(status);
    const DISCOUNT_PERCENT = Number(discountPercent) || 0;
    const totalCents = currentCartPayload.reduce((sum, variant) => {
      return sum + (Number(variant.price || 0) * Number(variant.quantity || 1));
    }, 0);
    const discountedCents = Math.round(totalCents * (1 - DISCOUNT_PERCENT / 100));
    // Discounted (sale) price → builder-total-price
    const priceEl = document.getElementById('builder-total-price');
    if (priceEl) {
      priceEl.innerText = formatMoney(discountedCents);
      priceEl.style.opacity = '1';
    }
    // Full (original) price → builder-price-original
    const originalEl = document.querySelector('.builder-price-original');
    if (originalEl) {
      originalEl.innerHTML = `<s>${formatMoney(totalCents)}</s>`;
      // Hide if no discount (i.e. cart is empty)
      originalEl.style.display = totalCents > 0 ? '' : 'none';
    }
    // Loyalty points based on discounted price
    const earnedPoints = Math.floor((discountedCents / 100) * 1);
    const pointsElement = document.getElementById('builder-loyalty-points');
    if (pointsElement) {
      pointsElement.innerText = earnedPoints;
    }
  }

  function publishSelection(status, reason) {
    window.builderSelection = currentCartPayload.map(item => ({
      id: item.id,
      product_id: item.product_id,
      sku: item.sku,
      title: item.title,
      price: item.price,
      quantity: item.quantity
    }));

    console.log(`==> SHOPIFY CART PAYLOAD:`, window.builderSelection);
    console.log(`FRONT-END UI STATE:`, window.localUIState);
    console.log(`ACTIVE THREEKIT CONFIGURATION:`, status?.attributes || {});
    console.log(`ACTIVE THREEKIT SKUS:`, status?.skus || {});
  }

  async function handleAddToCart() {
    const btn = document.getElementById('builder-add-to-cart');
    if (!btn) return;

    const btnText = btn.querySelector('.builder-btn-text');
    const btnLoading = btn.querySelector('.builder-btn-loading');

    btn.disabled = true;
    if (btnText) btnText.style.display = 'none';
    if (btnLoading) btnLoading.style.display = 'inline-block';

    hideError();

    try {
      let status = extension.getStatus();
      if (status instanceof Promise) status = await status;

      currentCartPayload = getSelectedVariants(status);

      if (!currentCartPayload.length) {
        throw new Error('No valid Shopify variants were mapped from this configuration.');
      }

      const unavailItem = currentCartPayload.find(v => isSkuUnavailable(v.sku));
      if (unavailItem) {
        throw new Error(`"${unavailItem.title.split(' - ')[0]}" is currently out of stock. Please remove it before adding to cart.`);
      }

      // Apply discount code silently before adding to cart
      const discountCode = 'CB-AT202615'; // <-- replace with your Shopify manual discount code CB-AT202615
      try {
        await fetch(`${Shopify.routes.root}discount/${encodeURIComponent(discountCode)}`, {
          method: 'GET',
          credentials: 'same-origin'
        });
      } catch (discountErr) {
        console.warn('Could not apply discount code:', discountErr);
      }

      let savedConfig = null;
      try {
        if (player && typeof player.saveConfiguration === 'function') {
          savedConfig = await player.saveConfiguration();
        }
      } catch (saveErr) {
        console.warn('Could not save Threekit configuration:', saveErr);
      }

      const bundleId = `TK-${Date.now()}`;

      const items = currentCartPayload.map(variant => {
        const properties = {
          _bundle_id: bundleId,
          _threekit_asset_id: assetId,
          _threekit_sku: variant.sku,
          _Configuration: JSON.stringify(window.localUIState || {})
        };

        if (savedConfig?.id) properties._threekit_config_id = savedConfig.id;
        if (savedConfig?.shortId) properties._threekit_config_short_id = savedConfig.shortId;

        return {
          id: Number(variant.id),
          quantity: Number(variant.quantity),
          properties
        };
      }).reverse();

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || 'Cart error');
      }

      window.location.href = `${Shopify.routes.root}cart`;
    } catch (err) {
      console.error(err);
      showError(err.message || 'Something went wrong.');
      btn.disabled = false;
      if (btnText) btnText.style.display = 'inline-block';
      if (btnLoading) btnLoading.style.display = 'none';
    }
  }

  function formatMoney(cents) {
    const formatString = document.body.getAttribute('data-money-format') || moneyFormat || '${{amount}}';

    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) return '0';

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsStr = parts[1] ? decimal + parts[1] : '';

      return dollars + centsStr;
    }

    const match = formatString.match(placeholderRegex);
    if (!match) return formatWithDelimiters(cents);

    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  function showError(msg) {
    const errorEl = document.getElementById('builder-error');
    if (errorEl) {
      errorEl.innerText = msg;
      errorEl.style.display = 'block';
    }
  }

  function hideError() {
    const errorEl = document.getElementById('builder-error'); 
    if (errorEl) {
      errorEl.style.display = 'none';
    }
  }

  function formatName(str) {
    return String(str ?? '').replace(/_/g, ' ');
  }

  function escapeHtml(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function buildSkeletonHTML() {
    // Mirrors the 5 bld-sections: Frame & Color, Cable System, Smith Machine, Accessories, Storage
    // First section (Frame & Color) is "open" showing a swatch row + 1 chip row
    // Others are closed — just the header bar
    const sk = (w) => `<span class="bld-skel" style="width:${w};height:inherit;display:block;"></span>`;

    const closedSection = (titleW = '110px') => `
      <div class="builder-skeleton-section">
        <div class="builder-skeleton-head">
          <div class="builder-skeleton-head-title bld-skel" style="width:${titleW};height:14px;"></div>
          <div class="builder-skeleton-head-chevron bld-skel" style="width:22px;height:14px;"></div>
        </div>
      </div>`;

    const skeletonRow = (titleW = '55%', priceW = '22%') => `
      <div class="builder-skeleton-row">
        <div class="builder-skeleton-thumb bld-skel"></div>
        <div class="builder-skeleton-text">
          <div class="builder-skeleton-title bld-skel" style="width:${titleW};"></div>
          <div class="builder-skeleton-price bld-skel" style="width:${priceW};"></div>
        </div>
      </div>`;

    return `<div class="builder-skeleton-wrap">

      <!-- Frame & Color — open, shows swatch + chip -->
      <div class="builder-skeleton-section">
        <div class="builder-skeleton-head">
          <div class="builder-skeleton-head-title bld-skel" style="width:130px;height:14px;"></div>
          <div class="builder-skeleton-head-chevron bld-skel" style="width:22px;height:14px;"></div>
        </div>
        <div class="builder-skeleton-body">
          <!-- Color swatches -->
          <div class="builder-skeleton-swatches">
            ${[28,28,28,28,28,28].map(w => `<div class="builder-skeleton-swatch bld-skel" style="width:${w}px;height:${w}px;"></div>`).join('')}
            <div class="bld-skel" style="width:80px;height:14px;margin-left:8px;"></div>
          </div>
          <!-- Chip row -->
          <div class="builder-skeleton-chips">
            <div class="bld-skel" style="width:55px;height:14px;margin-right:4px;align-self:center;"></div>
            ${['68px','72px','80px'].map(w => `<div class="builder-skeleton-chip bld-skel" style="width:${w};"></div>`).join('')}
          </div>
        </div>
      </div>

      <!-- Cable System — closed -->
      ${closedSection('120px')}

      <!-- Smith Machine — closed -->
      ${closedSection('130px')}

      <!-- Accessories — closed -->
      ${closedSection('105px')}

      <!-- Storage — closed -->
      ${closedSection('80px')}

    </div>`;
  }

  function escapeAttributeValue(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
});

window.addEventListener('pageshow', function (event) {
  if (event.persisted) {
    window.location.reload();
  }
});